'use client';

import { useState, useEffect, useRef, useCallback, Children, cloneElement } from 'react';
import { cn } from '../../lib/utils.js';

/**
 * ScrollSnapContainer - Enables fullpage scroll snapping behavior
 * Similar to the Rhye HTML template scroll behavior
 */
export function ScrollSnapContainer({
  children,
  showIndicator = true,
  className,
}) {
  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const sectionCount = Children.count(children);

  // Handle scroll and update active section
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout;

    const handleScroll = () => {
      if (isScrolling) return;

      const scrollTop = container.scrollTop;
      const sectionHeight = container.clientHeight;
      const newActiveSection = Math.round(scrollTop / sectionHeight);

      if (newActiveSection !== activeSection && newActiveSection >= 0 && newActiveSection < sectionCount) {
        setActiveSection(newActiveSection);
      }

      // Detect when scrolling stops
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [activeSection, sectionCount, isScrolling]);

  // Scroll to a specific section
  const scrollToSection = useCallback((index) => {
    const container = containerRef.current;
    if (!container || index < 0 || index >= sectionCount) return;

    setIsScrolling(true);
    const sectionHeight = container.clientHeight;

    container.scrollTo({
      top: index * sectionHeight,
      behavior: 'smooth'
    });

    setActiveSection(index);

    setTimeout(() => {
      setIsScrolling(false);
    }, 800);
  }, [sectionCount]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        scrollToSection(activeSection + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        scrollToSection(activeSection - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        scrollToSection(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        scrollToSection(sectionCount - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSection, sectionCount, scrollToSection]);

  return (
    <>
      <div
        ref={containerRef}
        className={cn('scroll-snap-container', className)}
      >
        {Children.map(children, (child, index) =>
          cloneElement(child, {
            'data-section-index': index,
            className: cn(child.props.className, 'scroll-snap-section'),
          })
        )}
      </div>

      {/* Page Indicator */}
      {showIndicator && (
        <PageIndicator
          total={sectionCount}
          active={activeSection}
          onDotClick={scrollToSection}
        />
      )}
    </>
  );
}

/**
 * PageIndicator - Shows dots for each section
 */
function PageIndicator({ total, active, onDotClick }) {
  return (
    <div className="page-indicator" role="navigation" aria-label="Sektionsnavigation">
      {Array.from({ length: total }, (_, index) => (
        <button
          key={index}
          onClick={() => onDotClick(index)}
          className={cn(
            'page-indicator__dot',
            active === index && 'page-indicator__dot--active'
          )}
          aria-label={`Gehe zu Sektion ${index + 1}`}
          aria-current={active === index ? 'true' : undefined}
        />
      ))}
    </div>
  );
}

/**
 * ScrollSnapSection - A single fullpage section
 */
export function ScrollSnapSection({
  children,
  className,
  variant = 'default',
  ...props
}) {
  const variantClasses = {
    default: 'bg-white',
    light: 'bg-secondary',
    dark: 'bg-primary text-white',
    accent: 'bg-accent text-white',
  };

  return (
    <section
      className={cn(
        'section-fullheight',
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export default ScrollSnapContainer;
